


















































































































































































































































































































import { defineComponent, computed, ref, watch, onActivated } from '@vue/composition-api';
import { SwapAsset } from '@nimiq/fastspot-api';
import { ArrowRightSmallIcon, AlertTriangleIcon, CircleSpinner, Tooltip } from '@nimiq/vue-components';
import { RouteName, useRouter } from '@/router';
import { nextTick } from '@/lib/nextTick';
import AccountBalance from '../AccountBalance.vue';
import AddressList from '../AddressList.vue';
import BitcoinIcon from '../icons/BitcoinIcon.vue';
import UsdcIcon from '../icons/UsdcIcon.vue';
import UsdtIcon from '../icons/UsdtIcon.vue';
import UsdtIconPadded from '../icons/UsdtIconPadded.vue';
import MenuIcon from '../icons/MenuIcon.vue';
import Amount from '../Amount.vue';
import FiatConvertedAmount from '../FiatConvertedAmount.vue';
import ConsensusIcon from '../ConsensusIcon.vue';
import MobileActionBar from '../MobileActionBar.vue';
import LegacyAccountNotice from '../LegacyAccountNotice.vue';
import LegacyAccountUpgradeButton from '../LegacyAccountUpgradeButton.vue';
import LegacyAccountNoticeModal from '../modals/LegacyAccountNoticeModal.vue';
// import OasisLaunchModal from '../swap/OasisLaunchModal.vue';
import AttentionDot from '../AttentionDot.vue';
import { backup, addAddress } from '../../hub';
import { useAccountStore, AccountType } from '../../stores/Account';
import { useBtcAddressStore } from '../../stores/BtcAddress';
import { usePolygonAddressStore } from '../../stores/PolygonAddress';
import { useWindowSize } from '../../composables/useWindowSize';
import { CryptoCurrency } from '../../lib/Constants';
import { useBtcNetworkStore } from '../../stores/BtcNetwork';
import { useSettingsStore } from '../../stores/Settings';
import { usePolygonNetworkStore } from '../../stores/PolygonNetwork';
import MiniAddIcon from '../icons/MiniAddIcon.vue';
import DoubleArrowIcon from '../icons/DoubleArrowIcon.vue';
import LinkedDoubleArrowIcon from '../icons/LinkedDoubleArrowIcon.vue';
import { useAddressStore } from '../../stores/Address';
import { useAccountSettingsStore } from '../../stores/AccountSettings';
// import { useStakingStore } from '../../stores/Staking';
// import AccountStake from '../staking/AccountStake.vue';

export default defineComponent({
    name: 'account-overview',
    setup() {
        const router = useRouter();
        const {
            activeAccountInfo,
            activeAccountId,
            setActiveCurrency,
            activeCurrency,
            hasBitcoinAddresses,
            hasPolygonAddresses,
        } = useAccountStore();
        const { accountBalance: nimAccountBalance } = useAddressStore();
        const { accountBalance: btcAccountBalance } = useBtcAddressStore();
        const {
            accountUsdcBridgedBalance,
            accountUsdcBalance,
            accountUsdtBridgedBalance,
        } = usePolygonAddressStore();
        const { stablecoin, knowsAboutUsdt } = useAccountSettingsStore();

        const isLegacyAccount = computed(() => (activeAccountInfo.value || false)
            && activeAccountInfo.value.type === AccountType.LEGACY);

        const canHaveMultipleAddresses = computed(() => (activeAccountInfo.value || false)
            && activeAccountInfo.value.type !== AccountType.LEGACY);

        const { isMobile, isTablet } = useWindowSize();

        function onAddressSelected() {
            setActiveCurrency(CryptoCurrency.NIM);

            if (isMobile.value) {
                router.push('/transactions');
            }
        }

        function selectBitcoin() {
            if (!hasBitcoinAddresses.value) return;

            setActiveCurrency(CryptoCurrency.BTC);

            if (isMobile.value) {
                router.push('/transactions');
            }
        }

        function selectStablecoin() {
            if (!hasPolygonAddresses.value || !stablecoin.value) return;

            if (stablecoin.value === CryptoCurrency.USDC && !knowsAboutUsdt.value) {
                router.push({ name: 'usdt-added' });
                return;
            }

            setActiveCurrency(stablecoin.value);

            if (isMobile.value) {
                router.push('/transactions');
            }
        }

        const showFullLegacyAccountNotice = computed(() =>
            isLegacyAccount.value
            && activeAccountInfo.value!.addresses.length === 1
            && !isTablet.value);

        const showModalLegacyAccountNotice = ref(false);

        function determineIfShowModalLegacyAccountNotice() {
            showModalLegacyAccountNotice.value = isLegacyAccount.value && isTablet.value;
        }

        function determineModalToShow() {
            determineIfShowModalLegacyAccountNotice();
        }

        watch(activeAccountInfo, determineModalToShow);

        const { consensus: btcConsensus } = useBtcNetworkStore();
        const { consensus: polygonConsensus } = usePolygonNetworkStore();

        const { updateAvailable } = useSettingsStore();

        const usdcAccount$ = ref<HTMLElement | null>(null);
        const nimiqAccount$ = ref<HTMLElement | null>(null);
        const bitcoinAccount$ = ref<HTMLElement | null>(null);
        const nimBtcSwapTooltip$ = ref<Tooltip | null>(null);
        const nimUsdcSwapTooltip$ = ref<Tooltip | null>(null);
        const btcUsdcSwapTooltip$ = ref<Tooltip | null>(null);

        const forceUpdateRef = ref(false);
        onActivated(forceUpdate); // to update on view change (settings <-> main view)

        async function forceUpdate() {
            await nextTick();
            // trick to force vue to update the position on component resize
            forceUpdateRef.value = !forceUpdateRef.value;
            /**
             * In some cases, the re-render after resize takes a bit too long
             * and the background svg is not positioned correctly.
             * (example: macos window maximize & minimize / toggling mobile view in devtools)
             * This is workaround to force to re-render the background svg and position it correctly.
             * This does the same as doing a setTimeout(() => forceUpdateRef.value = !forceUpdateRef.value, 0);
             */
            await nextTick();
            forceUpdateRef.value = !forceUpdateRef.value;
        }

        const isNimiqAddressListScrolling = computed(() => {
            // trick to force Vue to update the scrolling on component resize
            forceUpdateRef.value; // eslint-disable-line no-unused-expressions

            return nimiqAccount$.value && nimiqAccount$.value.scrollHeight > nimiqAccount$.value.clientHeight;
        });

        let timeoutid: any;
        function onSwapButtonPointerDown(event: TouchEvent, route: string) {
            clearTimeout(timeoutid);
            router.push(`/swap/${route}`);
            timeoutid = setTimeout(() => {
                ((event.target! as HTMLElement).parentNode as HTMLElement).focus();
            }, 100);
        }

        // const { totalAccountStake } = useStakingStore();

        return {
            stablecoin,
            activeAccountInfo,
            AccountType,
            backup,
            canHaveMultipleAddresses,
            addAddress,
            activeAccountId,
            onAddressSelected,
            nimAccountBalance,
            btcAccountBalance,
            accountUsdcBridgedBalance,
            accountUsdcBalance,
            accountUsdtBridgedBalance,
            showFullLegacyAccountNotice,
            showModalLegacyAccountNotice,
            selectBitcoin,
            selectStablecoin,
            activeCurrency,
            CryptoCurrency,
            SwapAsset,
            hasBitcoinAddresses,
            hasPolygonAddresses,
            btcConsensus,
            polygonConsensus,
            updateAvailable,
            usdcAccount$,
            nimiqAccount$,
            bitcoinAccount$,
            nimBtcSwapTooltip$,
            nimUsdcSwapTooltip$,
            btcUsdcSwapTooltip$,
            isNimiqAddressListScrolling,
            onSwapButtonPointerDown,
            isMobile,
            RouteName,
            // totalAccountStake,
        };
    },
    components: {
        ArrowRightSmallIcon,
        AlertTriangleIcon,
        AccountBalance,
        AddressList,
        BitcoinIcon,
        UsdcIcon,
        UsdtIcon,
        UsdtIconPadded,
        MenuIcon,
        ConsensusIcon,
        MobileActionBar,
        LegacyAccountNotice,
        LegacyAccountUpgradeButton,
        LegacyAccountNoticeModal,
        // OasisLaunchModal,
        Amount,
        FiatConvertedAmount,
        AttentionDot,
        CircleSpinner,
        MiniAddIcon,
        DoubleArrowIcon,
        Tooltip,
        LinkedDoubleArrowIcon,
        // StakingSummaryMobile,
        // AccountStake,
    },
});
